/**
 * PDP Zip Code Editor Component
 * Handles zip code display and editing for Estimated Delivery on PDP
 * Ticket: MDX-3052
 */

class PDPZipCodeEditor extends HTMLElement {
  constructor() {
    super();
    this.zipCodeManager = window.ZipCodeManager;
    this.isEditing = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.loadStoredZipCode();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  attachEventListeners() {
    // Listen for zip code updates from Cart
    this.boundHandleZipCodeUpdate = this.handleZipCodeUpdate.bind(this);
    window.addEventListener('zipcode:updated', this.boundHandleZipCodeUpdate);

    // Display click - toggle edit mode
    this.displayElement = this.querySelector('[data-pdp-zip-display]');
    if (this.displayElement) {
      this.displayElement.addEventListener('click', () => this.enterEditMode());
    }

    // Close edit mode button
    this.closeButton = this.querySelector('[data-pdp-zip-close]');
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.exitEditMode());
    }

    // Update button
    this.updateButton = this.querySelector('[data-pdp-zip-update]');
    if (this.updateButton) {
      this.updateButton.addEventListener('click', () => this.handleUpdate());
    }

    // Input field
    this.inputField = this.querySelector('[data-pdp-zip-input]');
    if (this.inputField) {
      this.inputField.addEventListener('input', (e) => this.handleInput(e));
      this.inputField.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleUpdate();
        }
        if (e.key === 'Escape') {
          this.exitEditMode();
        }
      });
    }
  }

  removeEventListeners() {
    if (this.boundHandleZipCodeUpdate) {
      window.removeEventListener('zipcode:updated', this.boundHandleZipCodeUpdate);
    }
  }

  /**
   * Loads stored zip code on page load
   */
  loadStoredZipCode() {
    const storedZip = this.zipCodeManager.get();
    if (storedZip) {
      this.updateDisplay(storedZip);
      this.updateDeliveryEstimate(storedZip);
    }
  }

  /**
   * Handles zip code updates from other pages (Cart)
   */
  handleZipCodeUpdate(event) {
    const { zipCode } = event.detail;
    if (zipCode) {
      this.updateDisplay(zipCode);
      this.updateDeliveryEstimate(zipCode);
    }
  }

  /**
   * Handles input changes
   * NOTE: This only validates input - does NOT auto-save or update display
   * User must click Update button to save changes
   */
  handleInput(event) {
    let value = event.target.value;
    // Only allow digits
    value = value.replace(/[^0-9]/g, '');
    // Limit to 5 digits
    value = value.slice(0, 5);
    event.target.value = value;

    // Clear error when user starts typing
    this.clearError();

    // Validate input format (does NOT save or update display)
    if (value.length === 5) {
      if (this.zipCodeManager.validate(value)) {
        this.clearError();
      } else {
        this.showError('Invalid ZIP code');
      }
    }
  }

  /**
   * Handles Update button click
   */
  handleUpdate() {
    const newZip = this.inputField?.value;
    const currentZip = this.zipCodeManager.get();

    // If no change, just exit edit mode
    if (newZip === currentZip) {
      this.exitEditMode();
      return;
    }

    // Validate
    if (!this.zipCodeManager.validate(newZip)) {
      this.showError('Please enter a valid 5-digit ZIP code');
      return;
    }

    // Save to localStorage
    this.zipCodeManager.set(newZip);

    // Update display
    this.updateDisplay(newZip);

    // Exit edit mode
    this.exitEditMode();

    // Update delivery estimate
    this.updateDeliveryEstimate(newZip);
  }

  /**
   * Enters edit mode
   */
  enterEditMode() {
    this.isEditing = true;
    this.classList.add('is-editing');

    // Pre-fill with current value
    const currentZip = this.zipCodeManager.get();
    if (this.inputField && currentZip) {
      this.inputField.value = currentZip;
    }

    // Focus input
    setTimeout(() => {
      this.inputField?.focus();
      this.inputField?.select();
    }, 100);
  }

  /**
   * Exits edit mode
   */
  exitEditMode() {
    this.isEditing = false;
    this.classList.remove('is-editing');
    this.clearError();
  }

  /**
   * Updates the displayed zip code
   */
  updateDisplay(zipCode) {
    const displayValue = this.querySelector('[data-pdp-zip-value]');
    if (displayValue) {
      displayValue.textContent = zipCode;
    }
  }

  /**
   * Shows error message
   */
  showError(message) {
    const errorElement = this.querySelector('[data-pdp-zip-error]');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
  }

  /**
   * Clears error message
   */
  clearError() {
    const errorElement = this.querySelector('[data-pdp-zip-error]');
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    }
  }

  /**
   * Updates delivery estimate based on zip code
   * TODO: Add zipCode parameter when implementing actual API call
   */
  updateDeliveryEstimate() {
    // TODO: Call shipping API to get actual delivery dates based on ZIP code

    // Placeholder: Update delivery date display
    const deliveryDateElement = document.querySelector('[data-delivery-estimate]');
    if (deliveryDateElement) {
      // TODO: Replace with actual API response
      // For now, calculate estimated delivery (example: 3-5 business days)
      const today = new Date();
      const minDays = 3;
      const maxDays = 5;

      const minDate = new Date(today);
      minDate.setDate(today.getDate() + minDays);

      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() + maxDays);

      const formatDate = (date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}`;
      };

      deliveryDateElement.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
    }
  }

  /**
   * Renders the component
   * No hardcoded/mock ZIP — shows '-' until a real ZIP is stored or
   * submitted. loadStoredZipCode() (called right after render()) will
   * replace it with the real value when one exists.
   */
  render() {
    const currentZip = this.zipCodeManager.get() || '-';

    this.innerHTML = `
      <div class="pdp-zip-code-editor">
        <!-- Display State -->
        <div class="pdp-zip-display" data-pdp-zip-display>
          <span class="pdp-zip-label">ZIP Code</span>
          <span class="pdp-zip-value" data-pdp-zip-value>${currentZip}</span>
          <svg class="icon icon-caret" aria-hidden="true" focusable="false" viewBox="0 0 10 6">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>
          </svg>
        </div>

        <!-- Edit Modal/Dropdown -->
        <div class="pdp-zip-editor" data-pdp-zip-editor>
          <div class="pdp-zip-editor-header">
            <h3>Enter ZIP Code</h3>
            <button type="button" class="pdp-zip-close" data-pdp-zip-close aria-label="Close">
              <svg viewBox="0 0 14 14" aria-hidden="true">
                <path d="M13 1L1 13M1 1l12 12" stroke="currentColor" stroke-width="2" fill="none"/>
              </svg>
            </button>
          </div>
          <div class="pdp-zip-editor-content">
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="5"
              placeholder="Enter ZIP"
              data-pdp-zip-input
              aria-label="ZIP code"
            />
            <button type="button" class="button button--primary" data-pdp-zip-update>
              Update
            </button>
          </div>
          <div class="pdp-zip-error" data-pdp-zip-error style="display: none;"></div>
        </div>
      </div>
    `;
  }
}

customElements.define('pdp-zip-code-editor', PDPZipCodeEditor);
